import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  Briefcase,
  Image as ImageIcon,
  LogOut,
  Mail,
  Megaphone,
  MenuIcon,
  MessageSquareQuote,
  Tags,
  Ticket,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";

const NAV = [
  { to: "/admin/overview", label: "Overview", icon: BarChart3 },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/jobs", label: "Jobs", icon: Briefcase },
  { to: "/admin/quote-inbox", label: "Quote Inbox", icon: MessageSquareQuote },
  { to: "/admin/testimonials", label: "Testimonials", icon: Tags },
  { to: "/admin/value-props", label: "Value Props", icon: ImageIcon },
  { to: "/admin/promo-popup", label: "Promo Popup", icon: Megaphone },
  { to: "/admin/coupons", label: "Coupons", icon: Ticket },
  { to: "/admin/email-log", label: "Email Log", icon: Mail },
];

export default function AdminLayout() {
  const { signOut } = useAdminAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [pathname]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const handleSignOut = () => {
    signOut();
    toast.success("Signed out of admin");
    navigate("/admin/login", { replace: true });
  };

  // Title shown in the mobile app bar.
  const sectionTitle = (() => {
    if (pathname.startsWith("/admin/overview")) return "Overview";
    if (pathname.startsWith("/admin/customers")) return "Customers";
    if (pathname.startsWith("/admin/jobs")) return "Jobs";
    if (pathname.startsWith("/admin/quote-inbox")) return "Quote Inbox";
    if (pathname.startsWith("/admin/testimonials")) return "Testimonials";
    if (pathname.startsWith("/admin/value-props")) return "Value Props";
    if (pathname.startsWith("/admin/promo-popup")) return "Promo Popup";
    if (pathname.startsWith("/admin/coupons")) return "Coupons";
    if (pathname.startsWith("/admin/email-log")) return "Email Log";
    return "Admin";
  })();

  return (
    <div
      className="min-h-screen bg-slate-50 text-slate-900"
      data-testid="admin-shell"
      style={{ fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" }}
    >
      {/* Mobile top bar — visible below md (<768) */}
      <header className="md:hidden sticky top-0 z-30 bg-slate-900 text-white border-b border-slate-800 flex items-center h-14 px-4 gap-3">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center justify-center h-11 w-11 rounded-lg border border-white/10 hover:bg-white/5 active:scale-95 transition-transform"
          aria-label="Open admin menu"
          data-testid="admin-mobile-toggle"
        >
          <MenuIcon size={18} />
        </button>
        <Link to="/admin/overview" className="flex items-center gap-2 min-w-0">
          <span
            className="inline-block h-7 w-7 rounded-full bg-[#D4A017] text-slate-900 grid place-items-center font-black text-[11px] shrink-0"
            aria-hidden="true"
          >
            SS
          </span>
          <span className="text-[14px] font-bold truncate">{sectionTitle}</span>
        </Link>
      </header>

      {/* Sidebar — fixed visible at md+, slides in on mobile */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen w-72 bg-slate-900 text-white border-r border-slate-800 transition-transform duration-300 ease-out md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-sidebar"
        aria-hidden={!open ? undefined : "false"}
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-slate-800">
          <Link to="/admin/overview" className="flex items-center gap-3" onClick={() => setOpen(false)}>
            <span
              className="inline-block h-9 w-9 rounded-full bg-[#D4A017] text-slate-900 grid place-items-center font-black text-sm"
              aria-hidden="true"
            >
              SS
            </span>
            <div className="leading-tight">
              <p
                className="text-[10px] uppercase tracking-[0.22em] text-[#D4A017] font-bold"
                style={{ fontFamily: "Outfit, ui-sans-serif" }}
              >
                Silky Smooth
              </p>
              <p className="text-sm font-bold text-white">Admin Console</p>
            </div>
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg border border-white/10 hover:bg-white/5 active:scale-95 transition-transform"
            aria-label="Close admin menu"
            data-testid="admin-mobile-close"
          >
            <X size={16} />
          </button>
        </div>

        <nav className="p-4 flex flex-col gap-1 overflow-y-auto h-[calc(100vh-4rem-72px)]">
          <p
            className="px-3 mt-1 mb-2 text-[10px] uppercase tracking-[0.24em] text-[#D4A017] font-bold"
            style={{ fontFamily: "Outfit, ui-sans-serif" }}
          >
            Operations
          </p>
          {NAV.slice(0, 4).map((item) => (
            <SidebarLink key={item.to} {...item} onClick={() => setOpen(false)} />
          ))}
          <p
            className="px-3 mt-5 mb-2 text-[10px] uppercase tracking-[0.24em] text-[#D4A017] font-bold"
            style={{ fontFamily: "Outfit, ui-sans-serif" }}
          >
            Content & Marketing
          </p>
          {NAV.slice(4, 8).map((item) => (
            <SidebarLink key={item.to} {...item} onClick={() => setOpen(false)} />
          ))}
          <p
            className="px-3 mt-5 mb-2 text-[10px] uppercase tracking-[0.24em] text-[#D4A017] font-bold"
            style={{ fontFamily: "Outfit, ui-sans-serif" }}
          >
            System
          </p>
          {NAV.slice(8).map((item) => (
            <SidebarLink key={item.to} {...item} onClick={() => setOpen(false)} />
          ))}
        </nav>

        <div className="absolute bottom-0 inset-x-0 p-4 border-t border-slate-800 bg-slate-900">
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 hover:bg-rose-600/90 text-white text-sm font-semibold px-4 py-2.5 transition-colors min-h-[44px]"
            data-testid="admin-signout"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Backdrop for mobile drawer */}
      {open && (
        <button
          type="button"
          aria-label="Dismiss admin menu"
          onClick={() => setOpen(false)}
          className="md:hidden fixed inset-0 z-30 bg-slate-950/60 backdrop-blur-sm"
          data-testid="admin-mobile-backdrop"
        />
      )}

      <main
        className="md:pl-72 min-h-screen overflow-x-clip"
        data-testid="admin-main"
      >
        <div className="px-4 sm:px-6 md:px-8 lg:px-10 py-6 md:py-9 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function SidebarLink({ to, label, icon: Icon, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
          isActive
            ? "bg-sky-600 text-white shadow-[0_2px_12px_-4px_rgba(14,165,233,0.5)]"
            : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
        }`
      }
      data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon size={16} className="shrink-0" />
      <span>{label}</span>
    </NavLink>
  );
}
